import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import FormatListNumberedOutlinedIcon from "@mui/icons-material/FormatListNumberedOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";

export const BannerCard = ({
  username,
  image,
  dis,
  type,
  active,
  order,
  onChangeOrder,
  onMoveUp,
  onMoveDown,
  isFirst = false,
  isLast = false,
  onEdit,
  onDelete,
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "20px",
        border: "1px solid",
        borderColor: alpha("#11409B", 0.1),
        bgcolor: "#fff",
        boxShadow: "0 4px 24px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
        height: "100%",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 40px rgba(17, 64, 155, 0.12)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 140,
          bgcolor: alpha("#11409B", 0.04),
          overflow: "hidden",
        }}
      >
        {image ? (
          <Box
            component="img"
            src={image}
            alt={username || "Banner"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", color: "#94a3b8" }}
          >
            <Typography variant="caption">No preview</Typography>
          </Stack>
        )}
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ position: "absolute", top: 10, left: 10 }}
        >
          {type && (
            <Chip
              label={type}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 700,
                bgcolor: alpha("#fff", 0.92),
                color: "#11409B",
                backdropFilter: "blur(8px)",
              }}
            />
          )}
          <Chip
            label={active !== false ? "Active" : "Inactive"}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              fontWeight: 700,
              bgcolor: alpha("#fff", 0.92),
              color: active !== false ? "#16a34a" : "#64748b",
              backdropFilter: "blur(8px)",
            }}
          />
        </Stack>

        {order != null && (
          <Tooltip title={onChangeOrder ? "Click to change display order" : "Display order"}>
            <Chip
              label={`Order #${order}`}
              size="small"
              onClick={onChangeOrder}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 800,
                bgcolor: "rgba(15, 23, 42, 0.82)",
                color: "#fff",
                backdropFilter: "blur(8px)",
                cursor: onChangeOrder ? "pointer" : "default",
                "&:hover": onChangeOrder ? { bgcolor: "#0f172a" } : {},
              }}
            />
          </Tooltip>
        )}
      </Box>

      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: "#0f172a",
            fontSize: "0.95rem",
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {username || "Untitled banner"}
        </Typography>
        {dis && (
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.8rem",
              mb: 1.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              minHeight: 40,
            }}
          >
            {dis}
          </Typography>
        )}

        <Stack direction="row" justifyContent="center" spacing={0.5}>
          {onChangeOrder && (
            <Tooltip title="Set display order">
              <IconButton
                onClick={onChangeOrder}
                size="small"
                sx={{
                  borderRadius: "10px",
                  border: `1px solid ${alpha("#7c3aed", 0.25)}`,
                  color: "#7c3aed",
                  "&:hover": { bgcolor: alpha("#7c3aed", 0.08) },
                }}
              >
                <FormatListNumberedOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {onMoveUp && (
            <Tooltip title="Move earlier in order">
              <span>
                <IconButton
                  onClick={onMoveUp}
                  disabled={isFirst}
                  size="small"
                  sx={{
                    borderRadius: "10px",
                    border: `1px solid ${alpha("#11409B", 0.15)}`,
                    color: "#11409B",
                    "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                  }}
                >
                  <ArrowBackIosNewOutlinedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onMoveDown && (
            <Tooltip title="Move later in order">
              <span>
                <IconButton
                  onClick={onMoveDown}
                  disabled={isLast}
                  size="small"
                  sx={{
                    borderRadius: "10px",
                    border: `1px solid ${alpha("#11409B", 0.15)}`,
                    color: "#11409B",
                    "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                  }}
                >
                  <ArrowForwardIosOutlinedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            onClick={onEdit}
            size="small"
            sx={{
              borderRadius: "10px",
              border: `1px solid ${alpha("#11409B", 0.15)}`,
              "&:hover": { bgcolor: alpha("#11409B", 0.06) },
            }}
          >
            <img src="/liEdit.svg" alt="Edit" />
          </IconButton>
          <IconButton
            onClick={onDelete}
            size="small"
            sx={{
              borderRadius: "10px",
              border: `1px solid ${alpha("#dc2626", 0.15)}`,
              "&:hover": { bgcolor: alpha("#dc2626", 0.06) },
            }}
          >
            <img src="/liDelet.svg" alt="Delete" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};
